import {
    CreditorInvoice,
    CreditorInvoiceSourceEnum,
    CreditorInvoiceStatusEnum,
    PaymentAttachmentsOutcome,
    PaymentInvoiceAllocation,
    PaymentInvoiceAllocationInput,
    SettlementInvoiceCandidate
} from '../models/Entities/CashFlow/CreditorInvoice/CreditorInvoice';
import { SettlementFileViewModel } from '../models/Entities/AzureFiles/AzureFileUpload';
import { StatusTone } from '../theme/guideTokens';
import { getExtension, isSameBlob, stripTenantSegment, validateFile, PRACTITIONER_INVOICE_ALLOWED_FILE_TYPES } from './AzureFileUpload.Functions';
import { AzureFileTypeEnum } from '../models/Enums/AzureFileTypeEnum';

/**
 * Facturas del profesional y su asignación a pagos (contrato §12): estado, sugerencias,
 * importes precargados y validaciones. Sin React, store ni HTTP.
 */

const round = (value: number): number => Math.round(value * 100) / 100;

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/** Lo aplicado por los pagos: el dato del servidor o, si no viene, la suma de sus pagos. */
export const appliedAmountOf = (invoice: Pick<CreditorInvoice, 'appliedAmount' | 'payments'>): number => {
    if (isFiniteNumber(invoice.appliedAmount)) return round(invoice.appliedAmount);
    return round((invoice.payments ?? []).reduce((acc, item) => acc + (isFiniteNumber(item.appliedAmount) ? item.appliedAmount : 0), 0));
};

/**
 * Estado de la factura. Usa el que calcula CashFlow (1..4) y, si no viene, lo calcula igual:
 * sin pagos → sin pago; sin importe y con pagos → asociada; aplicado < importe → parcial; si no → pagada.
 */
export const invoiceStatusOf = (invoice: Pick<CreditorInvoice, 'status' | 'amount' | 'appliedAmount' | 'payments'>): CreditorInvoiceStatusEnum => {
    const status = Number(invoice.status);
    if (status >= CreditorInvoiceStatusEnum.Unpaid && status <= CreditorInvoiceStatusEnum.Linked) return status as CreditorInvoiceStatusEnum;
    const hasPayments = (invoice.payments ?? []).length > 0 || appliedAmountOf(invoice) > 0;
    if (!hasPayments) return CreditorInvoiceStatusEnum.Unpaid;
    if (!isFiniteNumber(invoice.amount) || invoice.amount <= 0) return CreditorInvoiceStatusEnum.Linked;
    return appliedAmountOf(invoice) < invoice.amount ? CreditorInvoiceStatusEnum.PartiallyPaid : CreditorInvoiceStatusEnum.Paid;
};

export type InvoiceStatusKey = 'unpaid' | 'partial' | 'paid' | 'linked';

export const INVOICE_STATUS_KEYS: Record<CreditorInvoiceStatusEnum, InvoiceStatusKey> = {
    [CreditorInvoiceStatusEnum.Unpaid]: 'unpaid',
    [CreditorInvoiceStatusEnum.PartiallyPaid]: 'partial',
    [CreditorInvoiceStatusEnum.Paid]: 'paid',
    [CreditorInvoiceStatusEnum.Linked]: 'linked',
};

/** Tono y símbolo de la pastilla (el estado nunca depende sólo del color). */
export const INVOICE_STATUS_PILL: Record<CreditorInvoiceStatusEnum, { tone: StatusTone; symbol: string }> = {
    [CreditorInvoiceStatusEnum.Unpaid]: { tone: 'warning', symbol: '!' },
    [CreditorInvoiceStatusEnum.PartiallyPaid]: { tone: 'info', symbol: '◐' },
    [CreditorInvoiceStatusEnum.Paid]: { tone: 'success', symbol: '✓' },
    [CreditorInvoiceStatusEnum.Linked]: { tone: 'neutral', symbol: '○' },
};

/** Lo que falta pagar de la factura, en su moneda; undefined si no tiene importe cargado. */
export const pendingAmountOf = (invoice: Pick<CreditorInvoice, 'amount' | 'appliedAmount' | 'payments'>): number | undefined => {
    if (!isFiniteNumber(invoice.amount)) return undefined;
    return Math.max(0, round(invoice.amount - appliedAmountOf(invoice)));
};

/** Factura que todavía se puede asociar a un pago: sin pago o pagada parcialmente. */
export const isPendingInvoice = (invoice: CreditorInvoice): boolean => {
    const status = invoiceStatusOf(invoice);
    return status === CreditorInvoiceStatusEnum.Unpaid || status === CreditorInvoiceStatusEnum.PartiallyPaid;
};

/** Importe aplicado que se precarga al elegir la factura: lo pendiente (vacío si no tiene importe). */
export const prefillAppliedAmount = (invoice: CreditorInvoice): number | undefined => {
    const pending = pendingAmountOf(invoice);
    return pending !== undefined && pending > 0 ? pending : undefined;
};

const timeOf = (value: string | Date | null | undefined): number => {
    if (!value) return Number.MAX_SAFE_INTEGER;
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
};

/**
 * Facturas pendientes para un pago: primero las de las liquidaciones que se están pagando,
 * después el resto; dentro de cada grupo, las más viejas primero (por fecha de factura, luego id).
 */
export const suggestInvoicesForPayment = (invoices: CreditorInvoice[], settlementIds: (number | null | undefined)[] = []): CreditorInvoice[] => {
    const paying = new Set(settlementIds.filter((id): id is number => isFiniteNumber(id)));
    return (invoices ?? [])
        .filter(isPendingInvoice)
        .map((invoice) => ({ invoice, suggested: invoice.settlementId !== null && invoice.settlementId !== undefined && paying.has(invoice.settlementId) }))
        .sort((a, b) =>
            Number(b.suggested) - Number(a.suggested) ||
            timeOf(a.invoice.invoiceDate ?? a.invoice.created) - timeOf(b.invoice.invoiceDate ?? b.invoice.created) ||
            a.invoice.id - b.invoice.id)
        .map((item) => item.invoice);
};

/** La factura es de alguna de las liquidaciones que se están pagando. */
export const isSuggestedInvoice = (invoice: CreditorInvoice, settlementIds: (number | null | undefined)[] = []): boolean =>
    invoice.settlementId !== null && invoice.settlementId !== undefined && settlementIds.includes(invoice.settlementId);

export interface InvoiceKpis {
    unpaid: number;
    partial: number;
    paid: number;
    linked: number;
}

/** Cantidad de facturas por estado (chips del estado de cuenta). */
export const invoiceKpis = (invoices: CreditorInvoice[]): InvoiceKpis =>
    (invoices ?? []).reduce<InvoiceKpis>((acc, invoice) => {
        acc[INVOICE_STATUS_KEYS[invoiceStatusOf(invoice)]] += 1;
        return acc;
    }, { unpaid: 0, partial: 0, paid: 0, linked: 0 });

/** Ruta relativa al tenant de una factura (para previsualizar). */
export const invoicePathOf = (invoice: Pick<CreditorInvoice, 'path' | 'blobPath'>): string => invoice.path || stripTenantSegment(invoice.blobPath);

/** "Factura A-0001-00001234" o el nombre del archivo si no tiene número. */
export const invoiceLabel = (invoice: Pick<CreditorInvoice, 'invoiceNumber' | 'fileName' | 'fileExtension' | 'id'>): string => {
    const number = (invoice.invoiceNumber || '').trim();
    if (number) return number;
    const name = (invoice.fileName || '').trim();
    if (name) return invoice.fileExtension && !name.toLowerCase().endsWith(`.${invoice.fileExtension.toLowerCase()}`) ? `${name}.${invoice.fileExtension}` : name;
    return `#${invoice.id}`;
};

/**
 * Facturas de liquidación (Settlements) que todavía no están registradas como facturas del profesional:
 * activas (no dadas de baja) y sin una factura con el mismo archivo o el mismo id de archivo.
 */
export const settlementInvoiceCandidates = (files: SettlementFileViewModel[], invoices: CreditorInvoice[]): SettlementInvoiceCandidate[] => {
    const seen = new Set<string>();
    return (files ?? [])
        .filter((file) => file && file.status !== 2)
        .filter((file) => !(invoices ?? []).some((invoice) =>
            (invoice.source === CreditorInvoiceSourceEnum.SettlementFile && invoice.settlementFileId === file.id) ||
            isSameBlob(invoice.blobPath, stripTenantSegment(file.blobPath))))
        .filter((file) => {
            const key = (file.blobPath || '').toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        })
        .map((file) => ({
            settlementId: file.settlementId,
            settlementFileId: file.id,
            path: stripTenantSegment(file.blobPath),
            fileName: file.fileName,
            fileExtension: file.fileExtension || getExtension(file.fileName),
            uploadedAt: file.uploadedAt
        }))
        .sort((a, b) => (Date.parse(b.uploadedAt || '') || 0) - (Date.parse(a.uploadedAt || '') || 0));
};

/**
 * Texto de importe → número, con el mismo criterio que el formulario de pago: con coma y punto, el
 * último separador es el decimal ("1.234,56", "1,234.56", "1234.5"). Vacío → undefined; no numérico → NaN.
 */
export const parseOptionalAmount = (text: string | null | undefined): number | undefined => {
    const raw = (text ?? '').trim().replace(/\s/g, '');
    if (!raw) return undefined;
    const lastComma = raw.lastIndexOf(',');
    const lastDot = raw.lastIndexOf('.');
    const normalized = lastComma > lastDot ? raw.replace(/\./g, '').replace(',', '.') : raw.replace(/,/g, '');
    const value = Number(normalized);
    return Number.isFinite(value) ? round(value) : NaN;
};

export interface InvoiceFormValues {
    file?: Pick<File, 'name' | 'size'>;
    /** Al editar o al registrar una factura de liquidación no se pide archivo. */
    requireFile: boolean;
    invoiceNumber: string;
    invoiceDate?: Date;
    amountText: string;
    currencyCode?: string;
}

export const INVOICE_NUMBER_MAX_LENGTH = 50;

/** Errores del formulario de factura (claves i18n bajo `creditorInvoices.form.errors`). */
export const validateInvoiceForm = (values: InvoiceFormValues): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (values.requireFile) {
        if (!values.file) errors.file = 'fileRequired';
        else {
            const fileError = validateFile(values.file, AzureFileTypeEnum.PractitionerInvoice, PRACTITIONER_INVOICE_ALLOWED_FILE_TYPES);
            if (fileError) errors.file = fileError;
        }
    }
    if ((values.invoiceNumber || '').trim().length > INVOICE_NUMBER_MAX_LENGTH) errors.invoiceNumber = 'numberTooLong';
    const amount = parseOptionalAmount(values.amountText);
    if (amount !== undefined && (Number.isNaN(amount) || amount <= 0)) errors.amount = 'amountInvalid';
    if (amount !== undefined && !Number.isNaN(amount) && amount > 0 && !values.currencyCode) errors.currencyCode = 'currencyRequired';
    return errors;
};

/** Fecha local yyyy-MM-dd (DateOnly del servidor). */
export const toDateOnly = (date: Date | undefined): string | undefined => {
    if (!date || Number.isNaN(date.getTime())) return undefined;
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// ── Selección de facturas de un pago ──

export interface InvoiceSelection {
    invoiceId: number;
    /** Importe aplicado (texto del campo); vacío = asociada sin importe. */
    amountText: string;
}

/** Selección inicial al elegir una factura: lo pendiente precargado. */
export const selectionFor = (invoice: CreditorInvoice): InvoiceSelection => {
    const prefill = prefillAppliedAmount(invoice);
    return { invoiceId: invoice.id, amountText: prefill !== undefined ? String(prefill) : '' };
};

/** Errores por factura: importe negativo o que no es número. */
export const validateSelections = (selections: InvoiceSelection[]): Record<number, string> => {
    const errors: Record<number, string> = {};
    selections.forEach((item) => {
        const value = parseOptionalAmount(item.amountText);
        if (value !== undefined && (Number.isNaN(value) || value < 0)) errors[item.invoiceId] = 'appliedInvalid';
    });
    return errors;
};

/** Cuerpo de PUT payments/{id}/invoices. */
export const buildAllocationsPayload = (selections: InvoiceSelection[]): PaymentInvoiceAllocationInput[] =>
    selections.map((item) => {
        const value = parseOptionalAmount(item.amountText);
        return { creditorInvoiceId: item.invoiceId, appliedAmount: value === undefined || Number.isNaN(value) ? null : value };
    });

/** Aviso local (no bloquea): lo aplicado supera lo pendiente de la factura. */
export const exceedsPending = (invoice: CreditorInvoice, amountText: string, alreadyAppliedByThisPayment = 0): boolean => {
    const value = parseOptionalAmount(amountText);
    const pending = pendingAmountOf(invoice);
    if (value === undefined || Number.isNaN(value) || pending === undefined) return false;
    return value > round(pending + alreadyAppliedByThisPayment);
};

/** Selección a partir de las asignaciones actuales del pago (drawer "Facturas"). */
export const selectionsFromAllocations = (allocations: PaymentInvoiceAllocation[]): InvoiceSelection[] =>
    (allocations ?? []).map((item) => ({
        invoiceId: item.creditorInvoiceId,
        amountText: isFiniteNumber(item.appliedAmount) ? String(item.appliedAmount) : ''
    }));

/** Factura de una asignación: la que viene anidada o una armada con los datos planos. */
export const invoiceOfAllocation = (allocation: PaymentInvoiceAllocation, creditorId = ''): CreditorInvoice =>
    allocation.invoice ?? {
        id: allocation.creditorInvoiceId,
        creditorId,
        settlementId: allocation.settlementId ?? null,
        source: CreditorInvoiceSourceEnum.Uploaded,
        blobPath: '',
        fileName: allocation.fileName ?? '',
        fileExtension: '',
        invoiceNumber: allocation.invoiceNumber ?? null,
        invoiceDate: allocation.invoiceDate ?? null,
        amount: allocation.amount ?? null,
        currencyCode: allocation.currencyCode ?? null
    };

/** Total aplicado por moneda de factura (resumen de la sección del pago). */
export const appliedTotalsByCurrency = (selections: InvoiceSelection[], invoices: CreditorInvoice[]): { currency: string; amount: number }[] => {
    const totals = new Map<string, number>();
    selections.forEach((item) => {
        const invoice = invoices.find((candidate) => candidate.id === item.invoiceId);
        const value = parseOptionalAmount(item.amountText);
        if (!invoice || value === undefined || Number.isNaN(value)) return;
        const currency = (invoice.currencyCode || '').toUpperCase() || '—';
        totals.set(currency, round((totals.get(currency) ?? 0) + value));
    });
    return Array.from(totals.entries()).map(([currency, amount]) => ({ currency, amount }));
};

/** Algo del pago nuevo quedó sin guardar (facturas o comprobantes). */
export const hasAttachmentFailures = (outcome: PaymentAttachmentsOutcome | undefined): boolean =>
    !!outcome && (outcome.allocations?.status === 'failed' || outcome.receipts.some((item) => item.status !== 'uploaded'));
